/* Patch 22.3 — Public Verify Document Page
 * --------------------------------------------------
 * Anyone (auditor, bank officer, project owner) who receives a
 * DHR-issued PDF can scan the bottom-right QR or paste the 16-char
 * fingerprint here to confirm the document was actually produced
 * by this system, when, by which company, and what version.
 *
 * No authentication required — the verification endpoint only
 * returns audit-safe metadata that's already visible on the PDF.
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_LABEL = {
  sales_quotation:     'Sales Quotation',
  decision_narrative:  'Decision Narrative',
  audit_evidence_pack: 'Audit Evidence Pack',
  cfo_board_pack:      'CFO Board Pack',
};

const palette = {
  bg:          '#0b1220',
  card:        '#101a2e',
  border:      '#1e2a44',
  textMuted:   '#94a3b8',
  text:        '#e2e8f0',
  textStrong:  '#f8fafc',
  valid:       '#10b981',
  invalid:     '#ef4444',
  accent:      '#6366f1',
};

export default function VerifyDocument() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const initialFp = (params.get('fingerprint') || '').trim().toLowerCase();

  const [fingerprint, setFingerprint] = useState(initialFp);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const runVerify = async (fp) => {
    const cleaned = (fp || '').trim().toLowerCase();
    if (!cleaned) {
      setError('Fingerprint is required.');
      setResult(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/pdf-fingerprint/verify`,
        { fingerprint: cleaned });
      setResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Verification failed. Please try again.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-run when arriving via ?fingerprint=...
  useEffect(() => {
    if (initialFp) {
      runVerify(initialFp);
    }
  }, [initialFp]);

  const onSubmit = (e) => {
    e.preventDefault();
    // Update URL so the user can share the verified link.
    navigate(`/verify-document?fingerprint=${encodeURIComponent(fingerprint.trim().toLowerCase())}`,
             { replace: true });
    runVerify(fingerprint);
  };

  return (
    <div
      data-testid="verify-document-page"
      style={{
        minHeight: '100vh',
        background: `radial-gradient(circle at top left, ${palette.card} 0%, ${palette.bg} 70%)`,
        color: palette.text,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '48px 16px',
      }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <div style={{
            fontSize: 12, letterSpacing: 2, color: palette.textMuted,
            textTransform: 'uppercase', marginBottom: 8,
          }}>
            DHR Contractor ERP · Patch 22.3
          </div>
          <h1 style={{
            fontSize: 32, fontWeight: 700, color: palette.textStrong,
            margin: 0, letterSpacing: -0.5,
          }}>
            PDF Document Verification
          </h1>
          <p style={{ color: palette.textMuted, marginTop: 12, lineHeight: 1.6 }}>
            Validate the authenticity of any DHR-issued PDF. Scan the QR on the
            document footer, or paste the 16-character fingerprint below.
            Verification is public and exposes only audit-safe metadata.
          </p>
        </div>

        {/* Input card */}
        <form onSubmit={onSubmit}
          style={{
            background: palette.card,
            border: `1px solid ${palette.border}`,
            borderRadius: 14,
            padding: 24,
            marginBottom: 24,
          }}
        >
          <label htmlFor="fp-input"
            style={{
              display: 'block', fontSize: 13, color: palette.textMuted,
              marginBottom: 8, letterSpacing: 0.5,
            }}
          >
            Document Fingerprint
          </label>
          <div style={{ display: 'flex', gap: 12 }}>
            <input
              id="fp-input"
              data-testid="verify-fingerprint-input"
              type="text"
              placeholder="e.g. 36790ed8e8f3aaf4"
              value={fingerprint}
              onChange={(e) => setFingerprint(e.target.value)}
              style={{
                flex: 1,
                background: palette.bg,
                border: `1px solid ${palette.border}`,
                borderRadius: 8,
                color: palette.textStrong,
                padding: '12px 14px',
                fontSize: 15,
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              data-testid="verify-submit-btn"
              disabled={loading}
              style={{
                background: palette.accent,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '12px 22px',
                fontSize: 14,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                transition: 'opacity 0.15s ease',
              }}
            >
              {loading ? 'Verifying…' : 'Verify'}
            </button>
          </div>
          {error && (
            <div data-testid="verify-error"
              style={{ color: palette.invalid, marginTop: 12, fontSize: 13 }}>
              {error}
            </div>
          )}
        </form>

        {/* Result */}
        {result && <VerificationResult result={result} />}
      </div>
    </div>
  );
}

function VerificationResult({ result }) {
  const valid = !!result.valid;
  const borderColor = valid ? palette.valid : palette.invalid;
  return (
    <div
      data-testid={valid ? "verify-valid-card" : "verify-invalid-card"}
      style={{
        background: palette.card,
        border: `1px solid ${borderColor}`,
        borderLeft: `4px solid ${borderColor}`,
        borderRadius: 14,
        padding: 24,
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: valid ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
          color: borderColor, fontSize: 20, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {valid ? '✓' : '✕'}
        </div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: palette.textStrong }}>
            {valid ? 'Document Verified' : 'Fingerprint Not Recognised'}
          </div>
          <div style={{ fontSize: 12, color: palette.textMuted, marginTop: 2 }}>
            {valid
              ? 'This document was issued by DHR Contractor ERP.'
              : 'No matching record found. The PDF may be forged, altered, or generated outside the system.'}
          </div>
        </div>
      </div>

      {/* Always show the fingerprint */}
      <Field label="Fingerprint" value={result.fingerprint || '—'} mono />

      {valid && (
        <>
          <Field label="Document Type"
            value={DOC_LABEL[result.document_type] || result.document_type || '—'} />
          <Field label="Document Reference"
            value={result.document_reference || '—'} mono />
          <Field label="Issued by"
            value={result.company_name || '—'} />
          <Field label="Generated At"
            value={formatDate(result.generated_at)} />
          <Field label="Version" value={`v${result.version || 1}`} />

          {result.audit_meta_summary && Object.keys(result.audit_meta_summary).length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div style={{
                fontSize: 11, letterSpacing: 1, color: palette.textMuted,
                textTransform: 'uppercase', marginBottom: 8,
              }}>
                Snapshot Summary
              </div>
              <div data-testid="verify-meta-summary" style={{
                background: palette.bg, border: `1px solid ${palette.border}`,
                borderRadius: 8, padding: 12, fontSize: 13,
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                color: palette.text, lineHeight: 1.6,
              }}>
                {Object.entries(result.audit_meta_summary).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: palette.textMuted }}>{k}</span>
                    <span style={{ color: palette.textStrong }}>{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      padding: '10px 0',
      borderTop: `1px dashed ${palette.border}`,
    }}>
      <div style={{ color: palette.textMuted, fontSize: 13 }}>{label}</div>
      <div style={{
        color: palette.textStrong, fontSize: 14, fontWeight: 500,
        fontFamily: mono ? 'ui-monospace, "SF Mono", Menlo, monospace' : 'inherit',
        textAlign: 'right',
      }}>
        {value}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
}
